import React from 'react'
import { Text } from 'rsuite'
import { omit } from 'lodash-es'
import cx from 'classnames'
import {
  Checkbox,
  Select,
  TextInput,
  Radio,
  DateInput,
  DOW as Dow,
  FormikErrorMessage,
  TagPickerInput,
  CheckboxGroup,
  PhoneInput
} from 'shared/atoms'
import { FormikInputs, InputProps } from 'shared/atoms/InputUnits/interfaces'
import CheckPicker from 'shared/atoms/InputUnits/CheckPicker'
import './formField.scss'

export interface IFormField<V>
  extends FormikInputs<V>,
    React.HTMLAttributes<HTMLFormElement> {
  label?: string
  id?: string
  value?: string
  onClick?: () => void
  onChange?: (value: any) => void
  removeWrapperClass?: boolean
  inputProps: InputProps
  data?: { label: string; value: string }[]
}

function updateLabelName(myString: string): string {
  // Add a space before each capital letter, except the first one
  const withSpaces = myString.replaceAll(/([a-z])([A-Z])/g, '$1 $2')
  // Capitalize the first letter of each word
  const labelString = withSpaces.replaceAll(/\b\w/g, (char) =>
    char.toUpperCase()
  )
  return labelString
}

export function FormField<V>(
  props: Readonly<IFormField<V>>
): React.ReactElement {
  const {
    formik,
    name,
    label,
    id,
    onClick,
    placeholder,
    className,
    removeWrapperClass,
    inputProps: incomingInputProps,
    data = [],
    onChange
  } = props
  const inputProps: IFormField<V>['inputProps'] = {
    inputType: 'INPUT',
    dataType: 'string',
    hideLabel: false,
    hideButton: true,
    ...incomingInputProps
  }

  const conditionalInputProp = incomingInputProps?.as
    ? { as: incomingInputProps?.as }
    : {}

  const { inputType, isDisabled } = inputProps as {
    inputType:
      | 'INPUT'
      | 'CHECKBOX'
      | 'SELECT'
      | 'LABEL'
      | 'RADIO'
      | 'DATE'
      | 'DOW'
      | 'CHECKPICKER'
      | 'TAG_PICKER'
      | 'CHECKBOX_GROUP'
      | 'PHONE'
      | undefined
    isDisabled?: boolean
  }
  const defaultLabel = updateLabelName(name)

  const renderFormInput = () => {
    switch (inputType) {
      case 'INPUT':
        return (
          <TextInput
            id={id}
            formik={formik}
            name={name}
            isDisabled={isDisabled}
            dataType={inputProps.dataType}
            placeholder={placeholder}
            className={incomingInputProps?.className}
            {...conditionalInputProp}
          />
        )

      case 'PHONE':
        return (
          <PhoneInput
            id={id}
            formik={formik}
            name={name}
            isDisabled={isDisabled}
            dataType={inputProps.dataType}
            placeholder={placeholder}
            {...conditionalInputProp}
          />
        )

      case 'CHECKBOX':
        return <Checkbox formik={formik} name={name} isDisabled={isDisabled} />

      case 'DATE':
        return (
          <DateInput
            formik={formik}
            name={name}
            isDisabled={isDisabled}
            placeholder={placeholder}
          />
        )

      case 'RADIO':
        return (
          <Radio
            formik={formik}
            inputProps={{ ...omit(inputProps, 'inputType') }}
            name={name}
          />
        )

      case 'SELECT':
        return (
          <Select
            formik={formik}
            name={name}
            inputProps={{ ...omit(inputProps, 'inputType') }}
            onChange={onChange}
          />
        )

      case 'DOW':
        return <Dow name={name} formik={formik} isDisabled={isDisabled} />
      case 'LABEL':
        return <Text>{(formik?.values as Record<string, any>)[name]}</Text>
      case 'CHECKPICKER':
        return (
          <CheckPicker
            name={name}
            formik={formik}
            inputProps={{
              data,
              placeholder: placeholder ?? 'Select options',
              searchable: true,
              disabled: isDisabled,
              ...omit(inputProps, 'inputType')
            }}
          />
        )
      case 'TAG_PICKER':
        return (
          <TagPickerInput
            formik={formik}
            name={name}
            inputProps={{ ...omit(inputProps, 'inputType') }}
          />
        )

      case 'CHECKBOX_GROUP':
        return (
          <CheckboxGroup
            formik={formik}
            name={name}
            inputProps={{ ...omit(inputProps, 'inputType') }}
          />
        )

      default:
        return null
    }
  }

  return (
    <div className={cx({ formInputWrapper: !removeWrapperClass }, className)}>
      {inputProps.hideLabel ? null : (
        <p className="inputLabel">
          <label htmlFor={name}>
            {(label ?? defaultLabel).replace(/\s*\*$/, '')}
            {(label ?? defaultLabel).endsWith('*') && (
              <span className="requiredStar"> *</span>
            )}
          </label>
          {inputProps.hideButton ? null : (
            <button type="button" className="viewButton" onClick={onClick}>
              view details
            </button>
          )}
        </p>
      )}
      {renderFormInput()}
      <FormikErrorMessage name={name} />
    </div>
  )
}

export default FormField
